import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { mapProductToTable } from 'dto/runOrchestration/productToTable';
import { mapWorkflowToTable } from 'dto/runOrchestration/workflowToTable';
import { IRunOrchestrationSlice } from 'interfaces/runOrchestration/runOrchestrationState.interface';
import { IServerWorkflow } from 'interfaces/workflow.interface';
import { RootState } from 'redux/store';
import {
  fetchWorkflows,
  fetchWorkflowsNoLoading,
} from 'api/workflows/workflowsThunk';
import {
  IWorkflow,
  IWorkflowProduct,
  RUN_ORCHESTRARIONS_STATUS,
} from 'interfaces/runOrchestration/workflowRow';

export const SLICE_KEY = 'runOrchestrations';

const initialState: IRunOrchestrationSlice = {
  loading: false,
  workflows: [],
  notFilteredWorkflows: [],
  filterModel: null,
  selectedWorkflow: null,
  wizardActiveIndex: 0,
  isFilteringOnlyActive: false,
  filterQuery: null,
  updatedWorkflow: null,
  importModalSelectedFile: null,
};

const filterWorkflows = (
  workflows: IWorkflow[] | IWorkflowProduct[],
  searchParam: string
) => {
  if (!workflows) {
    return [];
  }

  const normalizedSearch = searchParam.toLowerCase();

  // Only iterate parent workflows (non-product rows). The grid will build the tree
  // from parent plus child rows. Avoid remapping products to keep stable IDs.
  const parentWorkflows = (
    workflows as (IWorkflow | IWorkflowProduct)[]
  ).filter((w) => !(w as IWorkflowProduct).isProduct) as IWorkflow[];

  const filtered: (IWorkflow | IWorkflowProduct)[] = parentWorkflows
    .map((workflow) => {
      const products =
        (workflow.products as IWorkflowProduct[] | undefined) || [];
      const filteredProducts = products.filter((product) =>
        product.name?.toLowerCase().includes(normalizedSearch)
      );

      const matchesWorkflowName = workflow.name
        ?.toLowerCase()
        .includes(normalizedSearch);

      const rows: (IWorkflow | IWorkflowProduct)[] = [];
      if (matchesWorkflowName || filteredProducts.length > 0) {
        if (filteredProducts.length > 0) {
          // Parent with only the matching products
          rows.push({ ...workflow, products: filteredProducts });
          filteredProducts.forEach((p) => rows.push(p));
        } else {
          // Parent matched: include it and all its children
          rows.push(workflow);
          products.forEach((p) => rows.push(p));
        }
      }
      return rows;
    })
    .flat();

  return filtered;
};

const findInsertIndexForNewWorkflow = (
  baseArray: IWorkflow[],
  newElement: IWorkflow
) => {
  let insertIndex = 0;
  insertIndex = baseArray?.findIndex(
    (w) => !w.isProduct && w.name.localeCompare(newElement.name) > 0
  );
  if (insertIndex === -1) {
    insertIndex = baseArray.length;
  }
  return insertIndex;
};

const createWorkflowItemsArray = (
  workflow: IWorkflow,
  hasProducts: boolean
): (IWorkflow | IWorkflowProduct)[] => {
  const items: (IWorkflow | IWorkflowProduct)[] = [workflow];
  if (hasProducts && workflow.products && workflow.products.length > 0) {
    items.push(...workflow.products);
  }
  return items;
};

const replaceWorkflowInArray = (
  originalArray: (IWorkflow | IWorkflowProduct)[],
  workflow: IWorkflow,
  newItems: (IWorkflow | IWorkflowProduct)[]
): (IWorkflow | IWorkflowProduct)[] => {
  const originalWorkflowIndex = originalArray.findIndex(
    (item) => !item.isProduct && item.id === workflow.id
  );

  if (originalWorkflowIndex === -1) {
    return [...originalArray, ...newItems];
  }

  let endIndex = originalWorkflowIndex + 1;
  while (
    endIndex < originalArray.length &&
    originalArray[endIndex]?.isProduct &&
    (originalArray[endIndex] as IWorkflowProduct)?.parentId === workflow.id
  ) {
    endIndex++;
  }

  return [
    ...originalArray.slice(0, originalWorkflowIndex),
    ...newItems,
    ...originalArray.slice(endIndex),
  ];
};

const runOrchestrationsSlice = createSlice({
  name: SLICE_KEY,
  initialState,
  reducers: {
    setWizardActiveIndex: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        wizardActiveIndex: state.wizardActiveIndex <= 3 ? action.payload : 3,
      };
    },
    clearState: (state) => {
      return {
        loading: false,
        workflows: [],
        notFilteredWorkflows: [],
        filterModel: null,
        selectedWorkflow: null,
        wizardActiveIndex: 0,
        isFilteringOnlyActive: false,
        filterQuery: null,
        updatedWorkflow: null,
        importModalSelectedFile: null,
      };
    },
    setSelectedWorkflow: (
      state,
      action: PayloadAction<IWorkflow | IWorkflowProduct | null>
    ) => {
      return {
        ...state,
        selectedWorkflow: action.payload,
      };
    },
    toggleFilteringOnlyActive: (state) => {
      const isDisplayingOnlyActive = !state.isFilteringOnlyActive;
      if (isDisplayingOnlyActive === true) {
        let filteredWorkflows: IWorkflow[] | IWorkflowProduct[] =
          state.workflows.filter(
            (item: IWorkflow | IWorkflowProduct) =>
              item.status === RUN_ORCHESTRARIONS_STATUS.ACTIVE
          );
        return {
          ...state,
          isFilteringOnlyActive: isDisplayingOnlyActive,
          workflows: filteredWorkflows,
        };
      } else {
        if (state.filterQuery != null && state.filterQuery !== '') {
          let filteredWorkflows: IWorkflow[] | IWorkflowProduct[] =
            filterWorkflows(state.notFilteredWorkflows, state.filterQuery);
          return {
            ...state,
            isFilteringOnlyActive: isDisplayingOnlyActive,
            workflows: filteredWorkflows,
          };
        } else {
          return {
            ...state,
            isFilteringOnlyActive: isDisplayingOnlyActive,
            workflows: state.notFilteredWorkflows,
          };
        }
      }
    },

    searchFilter: (state, action: PayloadAction<string | null>) => {
      const searchParams = action.payload;
      if (!searchParams) {
        return {
          ...state,
          filterQuery: searchParams,
          workflows: state.isFilteringOnlyActive
            ? state.notFilteredWorkflows.filter(
                (item: IWorkflow | IWorkflowProduct) =>
                  item.status === RUN_ORCHESTRARIONS_STATUS.ACTIVE
              )
            : state.notFilteredWorkflows,
        };
      }
      let filteredWorkflows: IWorkflow[] | IWorkflowProduct[] = filterWorkflows(
        state.notFilteredWorkflows,
        searchParams
      );
      return {
        ...state,
        filterQuery: searchParams,
        workflows: state.isFilteringOnlyActive
          ? filteredWorkflows.filter(
              (item: IWorkflow | IWorkflowProduct) =>
                item.status === RUN_ORCHESTRARIONS_STATUS.ACTIVE
            )
          : filteredWorkflows,
      };
    },
    addNewWorkflow: (state, action: PayloadAction<IServerWorkflow>) => {
      let workflow: IWorkflow = mapWorkflowToTable(action.payload);
      workflow.products = workflow.products
        ?.map((product) => mapProductToTable(product, workflow))
        .sort((a, b) => a.name.localeCompare(b.name));

      const existingWorkflowIndex = state.workflows.findIndex(
        (w) => w.name === workflow.name
      );

      if (existingWorkflowIndex !== -1) {
        let newWorkflows = [...state.workflows];
        let newNotFilteredWorkflows = [...state.notFilteredWorkflows];
        newWorkflows[existingWorkflowIndex] = workflow;
        const notFilteredIndex = newNotFilteredWorkflows.findIndex(
          (w) => w.name === workflow.name
        );
        if (notFilteredIndex !== -1) {
          newNotFilteredWorkflows[notFilteredIndex] = workflow;
        }
        return {
          ...state,
          workflows: newWorkflows,
          notFilteredWorkflows: newNotFilteredWorkflows,
          updatedWorkflow: action.payload,
        };
      } else {
        if (state.filterQuery != null && state.filterQuery !== '') {
          const notFilteredWorkflows: IWorkflow[] = state.notFilteredWorkflows;
          const updatedFilteredWorkflows = [...state.workflows];
          const updatedWorkflows = [...notFilteredWorkflows];
          let insertIndex = findInsertIndexForNewWorkflow(
            updatedFilteredWorkflows,
            workflow
          );
          updatedFilteredWorkflows.splice(insertIndex, 0, workflow);
          insertIndex = findInsertIndexForNewWorkflow(
            updatedWorkflows,
            workflow
          );
          updatedWorkflows.splice(insertIndex, 0, workflow);
          return {
            ...state,
            workflows: updatedFilteredWorkflows,
            notFilteredWorkflows: updatedWorkflows,
          };
        } else {
          const updatedWorkflows = [...state.workflows];
          let insertIndex = findInsertIndexForNewWorkflow(
            updatedWorkflows,
            workflow
          );
          updatedWorkflows.splice(insertIndex, 0, workflow);
          return {
            ...state,
            workflows: updatedWorkflows,
            notFilteredWorkflows: updatedWorkflows,
            updatedWorkflow: action.payload,
          };
        }
      }
    },
    deleteWorkflow: (state, action: PayloadAction<{ workflowId: number }>) => {
      if (state.filterQuery != null && state.filterQuery !== '') {
        const notFilteredWorkflows: IWorkflow[] = state.notFilteredWorkflows;
        const updatedWorkflows = [...notFilteredWorkflows].filter(
          (workflow) => workflow.id !== action.payload.workflowId
        );
        const updatedFilteredWorkflows = [...state.workflows].filter(
          (workflow) => workflow.id !== action.payload.workflowId
        );
        return {
          ...state,
          workflows: updatedFilteredWorkflows,
          notFilteredWorkflows: updatedWorkflows,
        };
      } else {
        const updatedWorkflows = [...state.workflows].filter(
          (workflow) => workflow.id !== action.payload.workflowId
        );
        return {
          ...state,
          workflows: updatedWorkflows,
          notFilteredWorkflows: updatedWorkflows,
        };
      }
    },
    updateWorkflow: (state, action: PayloadAction<IServerWorkflow>) => {
      const serverWorkflow = action.payload;
      const mappedWorkflow = mapWorkflowToTable(serverWorkflow);

      const hasProducts =
        (serverWorkflow.productGroup?.products?.length ?? 0) > 0;
      if (hasProducts && mappedWorkflow.products) {
        mappedWorkflow.products = mappedWorkflow.products
          .map((product) => mapProductToTable(product, mappedWorkflow))
          .sort((a, b) => a.name.localeCompare(b.name));
      } else {
        mappedWorkflow.products = null;
      }

      const workflowItems = createWorkflowItemsArray(
        mappedWorkflow,
        hasProducts
      );

      const updatedWorkflows = replaceWorkflowInArray(
        state.workflows,
        mappedWorkflow,
        workflowItems
      );
      const updatedNotFilteredWorkflows = replaceWorkflowInArray(
        state.notFilteredWorkflows,
        mappedWorkflow,
        workflowItems
      );

      return {
        ...state,
        workflows: state.isFilteringOnlyActive
          ? updatedWorkflows.filter(
              (item) => item.status === RUN_ORCHESTRARIONS_STATUS.ACTIVE
            )
          : updatedWorkflows,
        notFilteredWorkflows: updatedNotFilteredWorkflows,
        updatedWorkflow: serverWorkflow,
      };
    },
    resetUpdatedWorkflow: (state) => {
      return {
        ...state,
        updatedWorkflow: null,
      };
    },
    setSelectedWorkflowById: (state, action: PayloadAction<number>) => {
      const matchingWorkflow = state.workflows?.find(
        (workflow) => workflow.id === action.payload
      );
      return {
        ...state,
        selectedWorkflow: matchingWorkflow || state.selectedWorkflow,
      };
    },
    setImportModalSelectedFile: (
      state,
      action: PayloadAction<string | null>
    ) => {
      return {
        ...state,
        importModalSelectedFile: action.payload,
      };
    },
    clearImportModalSelectedFile: (state) => {
      return {
        ...state,
        importModalSelectedFile: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWorkflows.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(
      fetchWorkflows.fulfilled || fetchWorkflowsNoLoading.fulfilled,
      (state, action: any) => {
        if (action.payload?.length > 0) {
          let workflows: IWorkflow[] = (action.payload as IServerWorkflow[])
            ?.map((workflow: IServerWorkflow) => mapWorkflowToTable(workflow))
            .sort((a, b) =>
              a.name
                ?.toLocaleLowerCase()
                ?.localeCompare(b.name?.toLocaleLowerCase())
            );
          workflows = workflows.map((workflow: IWorkflow) => {
            const products = workflow.products?.map((product) =>
              mapProductToTable(product, workflow)
            );
            products?.sort((a, b) => a.name.localeCompare(b.name));
            return { ...workflow, products };
          });

          const flattenedWorkflows: (IWorkflow | IWorkflowProduct)[] = [];
          workflows.forEach((workflow) => {
            flattenedWorkflows.push(workflow);
            if (workflow.products && workflow.products.length > 0) {
              flattenedWorkflows.push(...workflow.products);
            }
          });

          if (state.filterQuery != null && state.filterQuery !== '') {
            let filteredWorkflows: IWorkflow[] | IWorkflowProduct[] =
              filterWorkflows(state.notFilteredWorkflows, state.filterQuery);
            return {
              ...state,
              loading: false,
              workflows: state.isFilteringOnlyActive
                ? [...filteredWorkflows].filter(
                    (item: IWorkflow | IWorkflowProduct) =>
                      item.status === RUN_ORCHESTRARIONS_STATUS.ACTIVE
                  )
                : filteredWorkflows,
              notFilteredWorkflows: flattenedWorkflows,
            };
          } else {
            return {
              ...state,
              loading: false,
              notFilteredWorkflows: flattenedWorkflows,
              workflows: state.isFilteringOnlyActive
                ? flattenedWorkflows.filter(
                    (item: IWorkflow | IWorkflowProduct) =>
                      item.status === RUN_ORCHESTRARIONS_STATUS.ACTIVE
                  )
                : flattenedWorkflows,
            };
          }
        } else {
          return {
            ...state,
            loading: false,
            workflows: [],
            notFilteredWorkflows: [],
          };
        }
      }
    );
    builder.addCase(fetchWorkflows.rejected, (state) => {
      return {
        ...state,
        loading: false,
      };
    });
  },
});

export const selectIsFileringOnlyActive = (state: RootState) =>
  state.runOrchestrations.isFilteringOnlyActive;

export const selectWorkflows = (state: RootState) =>
  state.runOrchestrations.workflows;
export const selectRunOrchestrationLoading = (state: RootState) =>
  state.runOrchestrations.loading;

export const selectSelectedWorkflow = (state: RootState) =>
  state.runOrchestrations.selectedWorkflow;

export const selectWizardActiveIndex = (state: RootState) =>
  state.runOrchestrations.wizardActiveIndex;

export const selectFilterQuery = (state: RootState) =>
  state.runOrchestrations.filterQuery;

export const selectUpdatedWorkflow = (state: RootState) =>
  state.runOrchestrations.updatedWorkflow;

export const selectWorkflowsIdsAndType = (state: RootState) =>
  state.runOrchestrations.workflows.map((workflow) => ({
    id: workflow.id,
    isProduct: workflow.isProduct,
  }));

export const selectWorkflowByName =
  (name: string) =>
  (state: RootState): IWorkflow | undefined =>
    state.runOrchestrations.workflows.find(
      (workflow) => workflow.name?.toLowerCase() === name.toLowerCase()
    );

export const selectAllWorkflowsNames = (state: RootState): string[] =>
  state.runOrchestrations.workflows?.map((workflow) => workflow.name);

export const selectWorkflowById =
  (id: number) =>
  (state: RootState): IWorkflow | undefined =>
    state.runOrchestrations.workflows.find(
      (workflow) => !workflow.isProduct && workflow.id === id
    );

export const selectImportModalSelectedFile = (state: RootState) =>
  state.runOrchestrations.importModalSelectedFile;

export type SelectIsFileringOnlyActiveType = ReturnType<
  typeof selectIsFileringOnlyActive
>;
export type SelectWorkflowsType = ReturnType<typeof selectWorkflows>;
export type SelectSelectedWorkflowType = ReturnType<
  typeof selectSelectedWorkflow
>;

export const {
  setWizardActiveIndex,
  clearState,
  setSelectedWorkflow,
  toggleFilteringOnlyActive,
  searchFilter,
  addNewWorkflow,
  deleteWorkflow,
  updateWorkflow,
  resetUpdatedWorkflow,
  setSelectedWorkflowById,
  setImportModalSelectedFile,
  clearImportModalSelectedFile,
} = runOrchestrationsSlice.actions;

export default runOrchestrationsSlice.reducer;
